import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { peopleGateway } from '../../index';
import { ApiError } from '../../../identity/types';
import { usePermissionCheck } from '../../../identity/state/usePermissions';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';
import type {
  AdminStateTaxResponse,
  AdminSubmittedStateDraft,
  StateIncomeTaxFieldsErrorCode,
  StateIncomeTaxFieldsResponse,
  StateTaxFormField,
} from '../../types';
import type { AdminStateTaxState } from './useAdminStateTax';

/** The three grants the tax engine field view needs together (the server requires all of them); display only, the server re-checks. */
export const FIELDS_PERMISSIONS = ['employees.read', 'taxpaymentsettings.read', 'jurisdictiontaxrules.read'] as const;
export const LOOKUP_PERMISSION = 'taxpaymentsettings.manage';

const NOT_AVAILABLE = 'Not available yet';

/** Calm, plain-language copy for each way the field list can be unavailable. `retry` is offered only where trying again can help. */
const ENGINE_MESSAGES: Record<StateIncomeTaxFieldsErrorCode, { text: string; retry: boolean }> = {
  NotConfigured: { text: "This preview is not turned on in this environment. Nothing is missing from the employee's record. Ask your administrator if you expected to see it.", retry: false },
  EnvironmentNotAllowed: { text: 'This preview is only available in the test environment. Nothing is missing from the employee\'s record.', retry: false },
  ProviderUnavailable: { text: 'We could not reach the tax partner\'s test service just now. Please try again in a moment.', retry: true },
  ProviderRejected: { text: 'The tax partner\'s test service could not answer this request. Please try again, and contact support if it keeps happening.', retry: true },
  UnexpectedResponse: { text: 'The tax partner\'s test service sent an answer we could not read. Please try again, and contact support if it keeps happening.', retry: true },
  DownstreamUnavailable: { text: 'Some of this employee\'s tax information could not be loaded just now. Please try again in a moment.', retry: true },
};

const MULTI_STATE_LABEL = { NotApplicable: 'One state', ReviewRequired: 'More than one state', Unresolved: 'Not known yet' } as const;

const TYPE_WORDS: Record<StateTaxFormField['dataType'], string | null> = { Integer: 'Whole number', Currency: 'Dollar amount', Boolean: 'Yes or no', Text: 'Text', Percentage: 'Percent', Unknown: null };

const formatDate = (value: string | null) => {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};
const money = (value: number) => `$${value.toFixed(2)}`;

function engineError(err: unknown): { text: string; retry: boolean } {
  if (err instanceof ApiError) {
    if (err.status === 403) return { text: "Your role can't view this section. Ask a company admin for access.", retry: false };
    if (err.status === 404) return { text: 'This employee could not be found.', retry: false };
    const code = err.code as StateIncomeTaxFieldsErrorCode | null;
    if (code && code in ENGINE_MESSAGES) return ENGINE_MESSAGES[code];
  }
  return { text: friendlyErrorMessage(err, 'This section could not be loaded. Please try again.'), retry: true };
}

function lookUpErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 501) return 'The tax jurisdiction look-up is not turned on in this environment. Ask your administrator.';
    if (err.status === 403) return "Your role can't do this look-up. Ask a company admin to do it.";
    if (err.status === 404) return 'The work location could not be found.';
    if ([400, 502, 503].includes(err.status) && err.messageIsServerAuthored) return err.message;
  }
  return friendlyErrorMessage(err, 'The tax jurisdictions could not be looked up. Please try again.');
}

function Fact({ label, value, note, testId }: { label: string; value: string | null; note?: string; testId: string }) {
  return (
    <div className="epx-fact" data-testid={testId}>
      <span className="epx-fact__label">{label}</span>
      <span className={value ? 'epx-fact__value' : 'epx-fact__value epx-fact__value--empty'}>{value ?? NOT_AVAILABLE}</span>
      {note && <span className="muted sit__note">{note}</span>}
    </div>
  );
}

type Attention = { level: 'attention' | 'empty' | 'ok'; label: string; summary: string; reasons: string[]; next: string };

/** One status for the whole page, with the reasons in words (never colour alone) and what to do next. Derived only from what the read returned. */
function attentionOf(tax: AdminStateTaxResponse): Attention {
  const reasons: string[] = [];
  if (tax.workState.status === 'Unknown') reasons.push('No work state yet: this employee has no primary work location, or it could not be looked up.');
  if (tax.multiState.status === 'ReviewRequired') reasons.push(`This employee is tied to more than one state (${tax.multiState.states.join(' and ')}). Review how state tax is withheld before payroll.`);
  if (tax.submittedByEmployee.draft?.matchesCurrentStates === false) reasons.push('The state the employee saved no longer matches where they work or live. Ask them to review it.');
  if (['SubmittedPendingReview', 'EligibleCertificateNeeded', 'NeedsReview', 'Rejected'].includes(tax.reciprocity.status)) {
    reasons.push(`Reciprocity: ${tax.reciprocity.label}.`);
  }
  if (reasons.length > 0) {
    return { level: 'attention', label: 'Needs review', summary: 'A few things need your attention.', reasons, next: 'Next: work through the items above. Reciprocity requests are reviewed in Tax Settings.' };
  }
  if (tax.submittedByEmployee.draft === null) {
    return { level: 'empty', label: 'Nothing submitted yet', summary: 'The employee has not saved any state tax answers.', reasons: [], next: 'Next: ask the employee to complete the Taxes step in their own setup. You do not need to do anything here.' };
  }
  return { level: 'ok', label: 'All set to review', summary: 'Nothing needs attention right now.', reasons: [], next: 'Next: review what the employee submitted below. State tax answers cannot be changed here yet.' };
}

function SubmittedBlock({ section }: { section: AdminStateTaxResponse['submittedByEmployee'] }) {
  const draft: AdminSubmittedStateDraft | null = section.draft;
  const additional =
    draft && (draft.additionalWithholdingValue !== null || draft.additionalWithholdingState !== null)
      ? `${draft.additionalWithholdingValue === null ? NOT_AVAILABLE : draft.additionalWithholdingType === 'Percent' ? `${draft.additionalWithholdingValue}%` : money(draft.additionalWithholdingValue)}${
          draft.additionalWithholdingType ? ` (${draft.additionalWithholdingType})` : ''
        }${draft.additionalWithholdingState ? ` for ${draft.additionalWithholdingState}` : ''}`
      : null;
  return (
    <section className="sit__block" aria-labelledby="sit-submitted-title">
      <h4 id="sit-submitted-title" className="sit__block-title">
        What the employee submitted <span className="sit__chip">Submitted by the employee, not yet used for payroll</span>
      </h4>
      {draft === null ? (
        <p className="muted" data-testid="sit-submitted-empty">
          Nothing submitted yet. The employee has not saved any state tax answers.
        </p>
      ) : (
        <dl className="sit__dl" data-testid="sit-submitted">
          <div><dt>State</dt><dd>{draft.stateName ?? draft.stateCode ?? NOT_AVAILABLE}</dd></div>
          <div><dt>Filing status</dt><dd>{draft.filingStatus ?? NOT_AVAILABLE}</dd></div>
          <div><dt>Allowances</dt><dd>{draft.allowances ?? NOT_AVAILABLE}</dd></div>
          <div><dt>Most recent withholding (for information)</dt><dd>{draft.mostRecentWithholdingAmount === null ? NOT_AVAILABLE : money(draft.mostRecentWithholdingAmount)}</dd></div>
          <div><dt>Extra withholding requested</dt><dd>{additional ?? 'None requested'}</dd></div>
          <div><dt>Last saved</dt><dd>{formatDate(draft.updatedAtUtc)}</dd></div>
        </dl>
      )}
    </section>
  );
}

function FieldCard({ field }: { field: StateTaxFormField }) {
  const type = TYPE_WORDS[field.dataType];
  return (
    <li className="sit__field" data-testid={`sit-field-${field.name}`}>
      <div className="sit__field-head">
        <strong>{field.label}</strong>
        {field.isRequired !== null && <span className="sit__tag">{field.isRequired ? 'Required' : 'Optional'}</span>}
        {type && <span className="sit__tag sit__tag--plain">{type}</span>}
      </div>
      {field.helpText !== null && <p className="muted sit__help">{field.helpText}</p>}
    </li>
  );
}

/** Support-only detail, kept available behind a disclosure: raw codes and values exactly as the tax partner sent them. */
function TechnicalDetails({ fields }: { fields: StateIncomeTaxFieldsResponse }) {
  return (
    <details className="sit__tech" data-testid="sit-technical">
      <summary>Technical details (for support)</summary>
      <p className="muted">
        Shown exactly as the tax partner&apos;s test service sent them. How the list of choices is written and what the validation type values mean have not been verified, so
        they are not interpreted. Nothing here is a default that EPN applies.
      </p>
      {fields.stateIncomeTax.map((group) => (
        <div key={group.taxItemId} className="sit__group">
          <h5 className="sit__group-title">{group.description} <code className="sit__code">{group.taxItemId}</code></h5>
          <ul className="sit__tech-list">
            {group.fields.map((f) => (
              <li key={f.name}>
                <code className="sit__code">{f.name}</code>
                <dl className="sit__dl sit__dl--compact">
                  <div><dt>Type</dt><dd>{f.dataType}</dd></div>
                  <div><dt>Entry</dt><dd>{f.isRequired === null ? 'Not stated' : f.isRequired ? 'Required' : 'Optional'}</dd></div>
                  {f.defaultValue !== null && <div><dt>Default (not applied)</dt><dd>default: {f.defaultValue === '' ? '(empty)' : f.defaultValue}</dd></div>}
                  {f.allowedValuesText !== null && <div><dt>Allowed values (as received)</dt><dd>{f.allowedValuesText}</dd></div>}
                  {f.validationPattern !== null && (
                    <div><dt>Validation pattern</dt><dd><code className="sit__code">{f.validationPattern}</code>{f.validationPatternDescription ? ` ${f.validationPatternDescription}` : ''}</dd></div>
                  )}
                  {f.validationKind !== null && <div><dt>Validation type (as received)</dt><dd>{f.validationKind}</dd></div>}
                  {f.formLine !== null && <div><dt>Form line</dt><dd>{f.formLine}</dd></div>}
                </dl>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {fields.taxItems.length > 0 && (
        <>
          <h5 className="sit__group-title">Tax items stored for this work location ({fields.taxItems.length})</h5>
          <ul className="sit__tech-list">
            {fields.taxItems.map((i) => (
              <li key={i.taxItemId}>
                {i.description} <code className="sit__code">{i.taxItemId}</code> · {i.isEmployerTax ? 'Employer tax' : 'Employee tax'}
                {i.isStateIncomeTax ? ' · Recognised as state income tax' : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="muted">As of {formatDate(fields.effectiveDate)}. {fields.selectionNote}</p>
    </details>
  );
}

/**
 * EPN-245: State Income Tax, read-only, written for a payroll admin. The page answers in order: where the employee works and lives, whether anything needs
 * attention (one status), what the employee submitted, reciprocity, and what the state asks of the employee. Raw field codes, ids and "as received" values sit
 * behind one collapsed Technical details disclosure. Nothing is calculated, defaulted, saved or edited here. A missing dependency (the work location's tax
 * jurisdictions) can be created in place (CLAUDE.md product principle). The server stays the authority for every permission.
 */
export function StateIncomeTaxSection({ employeeId, stateTax, onOpenReciprocityReview }: { employeeId: string; stateTax: AdminStateTaxState; onOpenReciprocityReview: () => void }) {
  const can = usePermissionCheck();
  const canView = FIELDS_PERMISSIONS.every((code) => can(code));
  const canLookUp = can(LOOKUP_PERMISSION);

  const [fields, setFields] = useState<StateIncomeTaxFieldsResponse | null>(null);
  const [fieldsError, setFieldsError] = useState<{ text: string; retry: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookUpError, setLookUpError] = useState<string | null>(null);
  const [lookUpDone, setLookUpDone] = useState(false);
  const panelHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    peopleGateway
      .getStateIncomeTaxFields(employeeId)
      .then((result) => {
        if (!cancelled) {
          setFields(result);
          setFieldsError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setFieldsError(engineError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, canView, attempt]);

  const retryFields = useCallback(() => {
    setFields(null);
    setFieldsError(null);
    setAttempt((n) => n + 1);
  }, []);

  async function lookUp(workLocationId: string) {
    setLookingUp(true);
    setLookUpError(null);
    try {
      await peopleGateway.lookUpWorkLocationTaxJurisdictions(workLocationId);
      setLookUpDone(true);
      setFields(null);
      setAttempt((n) => n + 1);
      // The button leaves the page once the profile exists: send focus to the panel heading so keyboard users are not dropped.
      panelHeading.current?.focus();
    } catch (err) {
      setLookUpError(lookUpErrorMessage(err));
    } finally {
      setLookingUp(false);
    }
  }

  const tax = stateTax.data;
  const noWageStates = tax
    ? [
        tax.reference.workStateHasNoWageIncomeTax && tax.workState.name ? tax.workState.name : null,
        tax.reference.residenceStateHasNoWageIncomeTax && tax.residenceState.name && tax.residenceState.name !== tax.workState.name ? tax.residenceState.name : null,
      ].filter((n): n is string => n !== null)
    : [];
  const attention = tax ? attentionOf(tax) : null;

  return (
    <section className="profile-section sit" aria-labelledby="sit-title">
      <h3 id="sit-title" className="profile-section-title">State Income Tax</h3>
      <p className="muted" data-testid="sit-readonly">
        Review only: you can see this information but not change it yet, and nothing in this section changes payroll. State withholding elections are recorded above.
      </p>

      {stateTax.failed ? (
        <>
          <p className="error" role="alert">We could not load this employee&apos;s state tax details. Please try again.</p>
          <button type="button" onClick={stateTax.reload}>Retry</button>
        </>
      ) : tax === null || attention === null ? (
        <p className="muted" role="status">Loading state tax details…</p>
      ) : (
        <>
          <div className={`sit__status sit__status--${attention.level}`} data-testid="sit-status" role="status">
            <p className="sit__status-head">
              <span className="sit__status-icon" aria-hidden="true">{attention.level === 'attention' ? '!' : attention.level === 'ok' ? '✓' : '–'}</span>
              <strong className="sit__status-label">{attention.label}</strong>
              <span>{attention.summary}</span>
            </p>
            {attention.reasons.length > 0 && <ul className="sit__reasons">{attention.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
            <p className="sit__next">{attention.next}</p>
          </div>

          <section className="sit__block" aria-labelledby="sit-where-title">
            <h4 id="sit-where-title" className="sit__block-title">Where this employee works and lives</h4>
            <div className="epx-facts" data-testid="sit-facts">
              <Fact label="Tax Work State" value={tax.workState.name ?? tax.workState.code} note={tax.workState.note} testId="sit-work-state" />
              <Fact label="Tax Residence State" value={tax.residenceState.name ?? tax.residenceState.code} note={tax.residenceState.note} testId="sit-residence-state" />
              <Fact label="States involved" value={MULTI_STATE_LABEL[tax.multiState.status]} testId="sit-multi-state" />
            </div>
            {noWageStates.length > 0 && (
              <p className="muted" data-testid="sit-no-wage-tax">
                For information: {noWageStates.join(' and ')} {noWageStates.length === 1 ? 'is' : 'are'} on EPN&apos;s draft list of states with no wage income tax. That list is a draft
                waiting for a payroll tax specialist to approve it, and it changes nothing.
              </p>
            )}
          </section>

          <SubmittedBlock section={tax.submittedByEmployee} />

          <section className="sit__block" aria-labelledby="sit-recip-title">
            <h4 id="sit-recip-title" className="sit__block-title">Reciprocity</h4>
            <p data-testid="sit-reciprocity">
              <strong>{tax.reciprocity.label}</strong>
              {tax.reciprocity.requiredForm ? ` · Certificate: ${tax.reciprocity.requiredForm}` : ''}
              {tax.reciprocity.submittedAtUtc ? ` · Submitted ${formatDate(tax.reciprocity.submittedAtUtc)}` : ''}
            </p>
            <button type="button" className="sit__link" onClick={onOpenReciprocityReview}>Open the reciprocity review in Tax Settings</button>
          </section>
        </>
      )}

      <section className="sit__block" aria-labelledby="sit-engine-title" data-testid="sit-engine">
        <h4 id="sit-engine-title" className="sit__block-title" ref={panelHeading} tabIndex={-1}>
          What this state asks of the employee
        </h4>
        <p className="muted">
          These are the questions this state&apos;s withholding form asks. They are shown so you know what to expect. Nothing here changes payroll.
        </p>

        <div aria-live="polite" className="sit__live">
          {!canView ? (
            <p data-testid="sit-engine-permission">Your role can&apos;t view this section. Ask a company admin for access.</p>
          ) : fieldsError ? (
            <>
              <p data-testid="sit-engine-error">{fieldsError.text}</p>
              {fieldsError.retry && <button type="button" onClick={retryFields}>Try again</button>}
            </>
          ) : fields === null ? (
            <p className="muted" role="status">Loading what this state asks…</p>
          ) : fields.status === 'NoWorkLocation' ? (
            <p data-testid="sit-engine-no-location">
              This employee has no primary work location yet, so we cannot tell which state to show.{' '}
              <Link className="sit__link" to={`/people/${employeeId}/employee-setup`}>Add a primary work location in Employee Setup</Link>.
            </p>
          ) : fields.status === 'NoLocationProfile' ? (
            <div data-testid="sit-engine-no-profile">
              <p>We need to look up the tax jurisdictions for this work location before we can show what the state asks.</p>
              {canLookUp && fields.workLocationId ? (
                <>
                  <button type="button" onClick={() => void lookUp(fields.workLocationId as string)} disabled={lookingUp}>
                    {lookingUp ? 'Looking up…' : 'Look up tax jurisdictions for this work location'}
                  </button>
                  <p className="muted sit__env">Test environment: this sends the work location&apos;s address to the tax partner&apos;s test service and saves the answer. Use synthetic data only. Nothing on this page is lost.</p>
                </>
              ) : (
                <p>Ask a company admin to look up tax jurisdictions for this work location.</p>
              )}
              {lookUpError && <p className="error" role="alert">{lookUpError}</p>}
            </div>
          ) : fields.status === 'NoStateIncomeTaxItem' ? (
            <div data-testid="sit-engine-no-item">
              <p>
                {noWageStates.length > 0
                  ? `We did not find a state income tax form for this location. That is expected for ${noWageStates[0]}, which is on our draft list of states with no wage income tax.`
                  : 'We did not find a state income tax form for this location, so there is nothing to show here yet. If you expected one, contact support and share the technical details below.'}
              </p>
            </div>
          ) : (
            <div data-testid="sit-engine-fields">
              {lookUpDone && <p className="muted" role="status">Tax jurisdictions were looked up for this work location.</p>}
              {fields.stateIncomeTax.map((group) => (
                <div key={group.taxItemId} className="sit__group">
                  {fields.stateIncomeTax.length > 1 && <h5 className="sit__group-title">{group.description}</h5>}
                  {group.fields.length === 0 ? (
                    <p className="muted">This state lists no questions for the employee.</p>
                  ) : (
                    <ul className="sit__fields">
                      {group.fields.map((f) => <FieldCard key={f.name} field={f} />)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {fields !== null && (fields.status === 'Available' || fields.status === 'NoStateIncomeTaxItem') && <TechnicalDetails fields={fields} />}
        </div>
        {!(fields?.status === 'NoLocationProfile' && canLookUp && fields.workLocationId) && (
          <p className="muted sit__env">Test environment: this comes from the tax partner&apos;s test service with synthetic data, so it is for review only.</p>
        )}
      </section>
    </section>
  );
}
